import React, { useState } from 'react';
import classnames from 'classnames';
import styles from './fileItem.module.scss';
import { Spinner } from 'react-bootstrap';
import magicWand from 'v2/icons/wand-magic-sparkles.svg';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from 'react-query';
import {
  AIConversation,
  AIConversationSearchType,
  File,
  FilePreprocessStatus,
  RootState,
} from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import { PERMISSION_NAME, hasPermission } from 'v2/utils/permissions';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import EditFileModal from '../FileModals/UpdateFileModal/EditFileModal';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import { AxiosError } from 'axios';

interface FileItemActionsProps {
  file: File;
}
const FileItemActions = ({ file }: FileItemActionsProps) => {
  const user = useSelector((state: RootState) => state.user.user);
  const permissions = useSelector(
    (state: RootState) => state.permissions.permissions
  );
  const [showFileModals, setShowFileModals] = useState({
    edit: false,
    delete: false,
  });
  const history = useHistory();
  const queryClient = useQueryClient();

  const { mutate: createSearch, isLoading: isSearchCreating } = useMutation(
    async () => {
      const data = {
        all_companies: false,
        all_files: false,
        files: [file.id],
        file_types: [file.type],
        search_type: AIConversationSearchType.DOCUMENT,
        data_vendors: file.relationships
          .filter((item) => item.table_name === 'DataVendor')
          .map((company) => company.object_id),
      };
      return await restFactory.post<AIConversation>(
        '/dj/api/v1/ai_conversation/',
        data
      );
    },
    {
      onSuccess: async (search) => {
        toast.success('Search created successfully');
        const params = new URLSearchParams({ search: `${search.id}` });
        history.push({
          pathname: '/ai-search',
          search: params.toString(),
        });
      },
      onError: () => {
        toast.error(
          'An error occurred while creating the Search. Please contact support'
        );
      },
    }
  );

  const { mutate: deleteFile, isLoading: isDeleting } = useMutation(
    () => restFactory.delete(`/dj/api/v1/file/${file.id}/`),
    {
      onSuccess: async () => {
        toast.success('File deleted successfully');
      },
      onError: (error: AxiosError | string) => {
        const errorMessage =
          typeof error === 'string'
            ? error
            : 'An error occurred while deleting the file. Please contact support';
        toast.error(errorMessage);
      },
      onSettled: () => {
        setShowFileModals({ edit: false, delete: false });
        onDelete();
      },
    }
  );

  const onDelete = () =>
    queryClient.refetchQueries(['FilesTabList'], {
      active: true,
    });

  const onUpdate = () => {
    setShowFileModals({ edit: false, delete: false });
    queryClient.refetchQueries(['FilesTabList', 'DataVendor'], {
      active: true,
    });
  };

  const isOwner = file.owner.id === user.id;
  const isPreProcessCompleted =
    file.preprocess_status === FilePreprocessStatus.COMPLETE;
  const hasChatPermission = hasPermission(permissions, [
    PERMISSION_NAME.DOCUMENT_CHAT,
  ]);
  const isFileNotPreprocessing =
    hasChatPermission &&
    file.preprocess_status !== FilePreprocessStatus.RUNNING;

  return (
    <div className={classnames(styles.actionButtons)}>
      {hasChatPermission && isPreProcessCompleted && (
        <button
          onClick={() => createSearch()}
          className={classnames(styles.createSearch)}
          disabled={isSearchCreating}
          aria-label="Create Search"
        >
          {isSearchCreating ? (
            <Spinner
              animation="border"
              size="sm"
              aria-label="loading-spinner"
            />
          ) : (
            <img src={magicWand} alt="magicWand" />
          )}
        </button>
      )}
      {isOwner && (!hasChatPermission || isFileNotPreprocessing) && (
        <>
          <button
            className={styles.delete}
            onClick={() => setShowFileModals({ ...showFileModals, edit: true })}
            data-testid="edit-file"
          >
            Edit
          </button>
          {showFileModals.edit && (
            <EditFileModal
              file={file}
              show={showFileModals}
              handleShow={setShowFileModals}
              onUpdate={onUpdate}
            />
          )}
          {showFileModals.delete && (
            <ConfirmationModal
              show={showFileModals.delete}
              onCancel={() => setShowFileModals({ edit: true, delete: false })}
              onOk={async () => deleteFile()}
              isLoading={isDeleting}
              okBtnText="Delete"
              title="Delete File"
            >
              <>Are you sure you want to delete this file?</>
            </ConfirmationModal>
          )}
        </>
      )}
    </div>
  );
};

export default FileItemActions;
